import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import { getPercent } from '@/shared/utils/formatters';
import Table, { HashrateCell, DateCell, TextCell, ExpandCell } from '@/shared/ui/Table';
import VisualStatus from '@/shared/ui/VisualStatus';
import { useGetMinerWorkersQuery } from '@/shared/api/Workers';
import { 
  selectDateRange,
  selectPollingInterval, 
} from "@/processes/Settings";
import { useMinerAccount } from "@/processes/MinerAccounts";
import { WorkerDetails } from '~/sha256/Workers';
import WorkersFilters, { FiltersSchema, checkWithIssues, FILTER_STATUS, FILTER_STATUSES_LENGTH, filterSignCompare, TH } from '~/shared/Workers/components/WorkersFilters';

type WorkersTableProps = {
  className?: string;
}

const WorkersTable = (props: WorkersTableProps) => {
  const intl = useIntl();
  const dateRange = useSelector(selectDateRange);
  const pollingInterval = useSelector(selectPollingInterval);
  const { account, miner, coin } = useMinerAccount();

  const initialFilters = { status: [] };
  const [filters, setFilters] = useState<FiltersSchema | any>(initialFilters);
  const { data = {}, isSuccess } = useGetMinerWorkersQuery({ 
    coin,
    account,
    miner,
    dateRange,
  }, { pollingInterval, skip: !(account || miner) });
  
  const workers = useMemo(() => {
    let workersFiltered = _get(data, 'workers', []);
    const {
      search,
      status,
      hashrate: filterHashrate,
      hashrateSign,
      sharesStale: filterSharesStale,
      sharesStaleSign,
      sharesInvalid: filterSharesInvalid,
      sharesInvalidSign,
    } = filters;
    if (search) { workersFiltered = workersFiltered.filter((worker: any) => worker.workerName.includes(search)) }

    // if all filters selected, skip filtrations
    if (status && status.length > 0 && status.length < FILTER_STATUSES_LENGTH) {
      // skip online, offline, if both selected
      if (!(status.includes(FILTER_STATUS.ONLINE) && status.includes(FILTER_STATUS.OFFLINE))) {
        if (status.includes(FILTER_STATUS.ONLINE)) { workersFiltered = workersFiltered.filter(({ online }: any) => online) }
        if (status.includes(FILTER_STATUS.OFFLINE)) { workersFiltered = workersFiltered.filter(({ offline }: any) => offline) }
      }
      if (status.includes(FILTER_STATUS.WITH_ISSSUES)) {
        workersFiltered = workersFiltered.filter((worker: any) => checkWithIssues(worker));
      }
    }

    if (filterHashrate) { 
      workersFiltered = workersFiltered.filter(({ hashrate24h }: any) => {
        return filterSignCompare(hashrate24h, filterHashrate * TH, hashrateSign);
      })
    }
    if (filterSharesStale) { 
      workersFiltered = workersFiltered.filter(({ staleCount }: any) => {
        return filterSignCompare(staleCount, filterSharesStale, sharesStaleSign);
      })
    }
    if (filterSharesInvalid) { 
      workersFiltered = workersFiltered.filter(({ invalidCount }: any) => {
        return filterSignCompare(invalidCount, filterSharesInvalid, sharesInvalidSign);
      })
    }
    
    return workersFiltered;
  }, [filters, data]); // eslint-disable-line

  return (
    <>
      <Table
        classNameRoot='mt-6'
        data={workers}
        withExpanded={true}
        ExpandedRow={WorkerDetails}
        responsiveColspan={{
          md: 4,
        }}
        virtualOptions={{
          estimateSize: { expandedTrHeight: { default: 400, md: 650 } },
        }}
        columns={[
          {
            Header: intl.formatMessage({ id: 'worker.name' }),
            accessor: 'workerName',
            Cell: (props: any) => {
              const { value, row, data } = props;
              const isOnline = _get(data, `${row.index}.online`, false);
              return (
                <span className='flex items-center break-all'>
                  <VisualStatus className='mr-2' status={isOnline ? 'success' : 'error'}/>
                  {value}
                </span>
              )
            }
          }, 
          {
            Header: intl.formatMessage({ id: 'worker.hashrate' }),
            accessor: 'hashrate',
            Cell: HashrateCell,
          }, 
          {
            Header: intl.formatMessage({ id: 'worker.hashrate24' }),
            accessor: 'hashrate24h',
            className: 'hidden md:table-cell',
            Cell: HashrateCell,
          }, 
          {
            Header: intl.formatMessage({ id: 'worker.valid.shares' }),
            className: 'hidden md:table-cell',
            accessor: 'validCount',
            Cell: ({ value, cell }: any) => {
              const { validRate } = cell.row.original;
              return (
                <TextCell value={`${value}`} subValue={validRate ? getPercent(validRate) : ''}/>
              )
            }
          }, 
          {
            Header: intl.formatMessage({ id: 'worker.stale.shares' }),
            accessor: 'staleCount',
            className: 'hidden md:table-cell',
            Cell: ({ value, cell }: any) => {
              const { staleRate } = cell.row.original;
              return (
                <TextCell value={value} subValue={staleRate ? getPercent(staleRate) : ''}/>
              )
            }
          }, 
          {
            Header: intl.formatMessage({ id: 'worker.invalid.shares' }),
            accessor: 'invalidCount',
            className: 'hidden md:table-cell',
            Cell: ({ value, cell }: any) => {
              const { invalidRate } = cell.row.original;
              return (
                <TextCell value={value} subValue={invalidRate ? getPercent(invalidRate) : ''}/>
              )
            }
          },
          {
            Header: intl.formatMessage({ id: 'worker.share.date' }),
            accessor: 'lastShareDt',
            Cell: DateCell,
          },
          {
            Header: () => (
              <WorkersFilters
                values={filters}
                onApply={setFilters}
                onClear={() => { setFilters(initialFilters) }}
                className="w-[30px]"
              />
            ),
            accessor: 'icon',
            disableSortBy: true,
            Cell: ExpandCell,
            width: 50,
          }
        ]}
      />
    </>
  );
}

export default WorkersTable;
